import { FlyoutContext } from "@app/shared/Flyouts";
import { FC, useContext, useRef, useCallback, useState, JSX } from "react";
import DragAndDropFileUploader from "@app/shared/DragAndDropFileUploader";
import { DocumentTypes } from "@app/admin/specialty-group-exception/documentTypesEnum";
import SpecialtyExceptionsService from "@app/admin/specialty-group-exception/specialtyExceptionsService";

interface AddExceptionsFlyoutProps {
  container: string;
  fileExtensionSplitter: (filename: string) => [string, string];
  onUpload: (displayTitle: string, response: string) => void;
  documentType: DocumentTypes;
}

const AddExceptionsFlyout: FC<AddExceptionsFlyoutProps> = ({ fileExtensionSplitter, onUpload, documentType }: AddExceptionsFlyoutProps): JSX.Element => {
  const supportService = useRef(new SpecialtyExceptionsService()).current;
  const flyoutContext = useContext(FlyoutContext);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleUpload = useCallback(async (data: FormData) => {
    const info: FormDataEntryValue = data.get("exceptionFile");
    if (info instanceof File) {
      if (info.size > 2000000000) {
        setErrorMessage("File is too large to upload.");
        return Promise.reject("file too large");
      }
      const [, extension] = fileExtensionSplitter(info.name);
      if (extension.toLowerCase() !== "pdf") {
        return Promise.reject("invalid file type");
      }
      try {
        const newDataStr = await supportService.uploadCmsExceptionTemplate(documentType, info);
        onUpload(info.name, newDataStr);
        flyoutContext.handleCloseFlyout();
      } catch (error) {
        setErrorMessage("Upload failed. Please try again.");
      }
    }
  }, [fileExtensionSplitter, onUpload, documentType, supportService, flyoutContext]);

  return (
    <>
      <p className="mt-4">Select the file you want to upload. Only PDF files are allowed.</p>
      {errorMessage && <p className="error-message">{errorMessage}</p>}
      <DragAndDropFileUploader
        apiKeyName="exceptionFile"
        onUpload={handleUpload}
        testId="exceptions__file-input"
      />
    </>
  );
};

export default AddExceptionsFlyout;
